import { useRef, useState, useEffect, FC, MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, ButtonBase, CardContent, ClickAwayListener, Grid, Paper, Popper, Stack, Tooltip, Typography } from '@mui/material';

// project import
import ProfileTab from './ProfileTab'; // Assuming this component is typed/exists
import MainCard from 'components/MainCard'; // Assuming this component is typed/exists
import Transitions from 'components/@extended/Transitions'; // Assuming this component is typed/exists
import IconButton from 'components/@extended/IconButton'; // Assuming this component is typed/exists

import useAuth from 'hooks/useAuth'; // Assuming this hook is typed/exists
import { ThemeMode } from 'config';
import { DASHBOARD_MODE_EVENT, DashboardModes, getDashboardMode } from 'utils/dashboardMode';
import { useConsultantProfile } from 'api/consultant'; // Assuming this hook is typed/exists

import { LogoutOutlined } from '@ant-design/icons';

interface UserInformation {
  preferedName?: string;
  preferredName?: string;
  name?: string;
  firstName?: string;
  username?: string;
  lastName?: string;
  cityName?: string;
  userType?: string;
  isConsultant?: boolean;
  Consultant?: {
    isConsultant?: boolean;
    name?: string;
    logo?: string;
    logoUrl?: string;
  };
  consultant?: {
    name?: string;
  };
  consultantName?: string;
  companyName?: string;
  [key: string]: any; 
}

interface AuthContextType {
  user: UserInformation | null;
  logout: () => Promise<void>;
}
const useAuthHook = useAuth as () => any;

interface UseConsultantProfileResult {
  consultant: any | null;
}
const useConsultantProfileHook = useConsultantProfile as (options: { enabled: boolean }) => UseConsultantProfileResult;

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { logout, user } = useAuthHook();
  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';
  const getStoredUser = () => {
    if (typeof window === 'undefined') return undefined;
    try {
      const rawInfo = localStorage.getItem('userInformation');
      const rawAuth = localStorage.getItem('authUser');
      const parsedInfo = rawInfo ? (JSON.parse(rawInfo) as UserInformation) : undefined;
      const parsedAuth = rawAuth ? (JSON.parse(rawAuth) as UserInformation) : undefined;
      return parsedInfo || parsedAuth || undefined;
    } catch (e) {
      console.error('Error parsing stored user', e);
      return undefined;
    }
  };

  const [userInformation, setUserInformation] = useState<UserInformation | undefined>(() => {
    if (user) return user as UserInformation;
    return getStoredUser();
  });
  const [dashboardMode, setDashboardMode] = useState<any | null>(() =>
    typeof window === 'undefined' ? null : (getDashboardMode() as any | null)
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = () => {
      try {
        const parsed = getStoredUser();
        setUserInformation(parsed);
      } catch (error) {
        console.error('Error parsing userInformation from localStorage:', error);
        setUserInformation(undefined);
      }
    };

    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (user) {
      setUserInformation(user as UserInformation);
    }
  }, [user]);
  useEffect(() => {
    const handleModeChange = (event: CustomEvent<{ mode: any }> | Event) => {
      const customEvent = event as CustomEvent<{ mode: any }>;
      const next = customEvent?.detail?.mode ?? (getDashboardMode() as any | null);
      setDashboardMode(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener(DASHBOARD_MODE_EVENT, handleModeChange as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(DASHBOARD_MODE_EVENT, handleModeChange as EventListener);
      }
    };
  }, []);

  const currentUser = (user as UserInformation | undefined) || userInformation;

  const displayName =
    currentUser?.preferedName ||
    currentUser?.preferredName ||
    currentUser?.name ||
    currentUser?.firstName ||
    currentUser?.username ||
    currentUser?.lastName ||
    '';

  const consultantLabel =
    userInformation?.Consultant?.name ||
    userInformation?.consultant?.name ||
    userInformation?.consultantName ||
    userInformation?.companyName ||
    '';
  const secondaryLabel = dashboardMode === DashboardModes.CONSULTANT ? consultantLabel : userInformation?.cityName || '';

  const isConsultantUser = Boolean(userInformation?.Consultant?.isConsultant || userInformation?.isConsultant);
  const { consultant } = useConsultantProfileHook({ enabled: isConsultantUser });
  const consultantLogo = consultant?.logo || userInformation?.Consultant?.logo || userInformation?.Consultant?.logoUrl || '';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          bgcolor: open ? iconBackColorOpen : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter' },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 0.5 }}>
          {isConsultantUser && consultantLogo && (
            <img src={consultantLogo} alt="Consultant logo" style={{ height: 18, width: 'auto', objectFit: 'contain' }} />
          )}
          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
            {displayName}
            {secondaryLabel ? `, ${secondaryLabel}` : ''}
          </Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: 290,
                minWidth: 240,
                maxWidth: 290,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 250
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          {/* <Avatar alt="profile user" src={avatar1} sx={{ width: 32, height: 32 }} /> */}
                          <Stack>
                            <Typography variant="h6">{displayName}</Typography>
                            {/* <Typography variant="body2" color="textSecondary">
                                                            UI/UX Designer
                                                        </Typography> */}
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid >
                        <Tooltip title="Logout">
                          <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogout}>
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Box sx={{ px: 2.5, pb: 3 }}>
                    <ProfileTab handleLogout={handleLogout} />
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
